import { useEffect, useMemo, useState } from 'react'

import { stringify } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_GAS_ESTIMATES } from '~/constants/refetch-intervals'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import {
  getSplitsErrorCodeString,
  useServerRequests,
} from '~/hooks/useRequests'
import { BackendTransactionDecodedCall, BasicTransactionType } from '~/types'

export type GasEstimateResponse = {
  gasEstimates: {
    callGasLimit: string
    verificationGasLimit: string
    preVerificationGas: string
    maxFeePerGas: string
    maxPriorityFeePerGas: string
  }
  isSponsored: boolean
}

export const useGasEstimates = (
  smartAccountId: string,
  {
    chainId,
    calls,
    transactionTypes,
  }: {
    chainId: SupportedChainId
    calls: BackendTransactionDecodedCall[]
    transactionTypes: BasicTransactionType[]
  },
) => {
  const { serverPostRequest } = useServerRequests()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [disabledAfterInvalid, setDisabledAfterInvalid] = useState(false)

  const serializedCalls = useMemo(() => stringify(calls), [calls])

  const isWalletConnectOrSplitsConnect = useMemo(
    () =>
      transactionTypes.length === 1 &&
      (transactionTypes[0] === BasicTransactionType.WalletConnect ||
        transactionTypes[0] === BasicTransactionType.SplitsConnect),
    [transactionTypes],
  )

  const queryResult = useQueryWrapper<GasEstimateResponse>({
    queryKey: [
      QUERY_KEYS.GAS_ESTIMATES,
      chainId,
      smartAccountId,
      serializedCalls,
      transactionTypes,
      calls.length,
    ],
    enabled: !disabledAfterInvalid,
    refetchInterval: REFETCH_GAS_ESTIMATES,
    queryFn: async () => {
      const transactionData = {
        chainId,
        calls: serializedCalls,
        smartAccountId,
        transactionTypes,
      }

      if (calls.length === 0) {
        throw new Error('No calls to estimate gas')
      }

      const result = await serverPostRequest<GasEstimateResponse>({
        path: '/api/smart_accounts/transactions/estimate_gas',
        params: { ...transactionData },
        onError: (error) => {
          // Detect invalid transaction error and disable future estimates
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const err: any = error
          if (getSplitsErrorCodeString(err) === 'INVALID_TRANSACTION_ERROR') {
            setErrorMessage(
              'This transaction is no longer valid. Please recreate it.',
            )
            setDisabledAfterInvalid(true)
          }
        },
        errorMessage: 'Failed to estimate transaction gas',
      })

      if (!result) {
        throw new Error('Failed to estimate transaction gas')
      }

      return result.data
    },
  })

  useEffect(() => {
    if (isWalletConnectOrSplitsConnect) {
      if (queryResult.status === 'error') {
        setErrorMessage(
          'We are unable to estimate the gas for this transaction. You may need to recreate it in the connected application. Please reach out to support@splits.org with any questions.',
        )
      } else {
        setErrorMessage(undefined)
      }
    }
  }, [isWalletConnectOrSplitsConnect, queryResult.status])

  return { ...queryResult, errorMessage }
}
